import { inject, Injectable } from '@angular/core';
import { debug_universal_locations, debug_backdrops, debug_sounds } from './script_service_debug';
import { Observable, of, from } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface Author {
  id: number;
  name: string;
  avatar: string;
}

export interface PublicScript {
  id: number;
  title: string;
  author: Author;
  playerCount: number;
  categories: string[];
  status?: number;
  isAnyVersionPublished?: boolean;
  isOnlyDrafts?: boolean;
  contentPreview?: string;
  viewCount?: number;
  ratingAverage?: number;
  ratingCount?: number;
}

export interface Backdrop {
  id: number;
  name: string;
  img_url: string;
  locations: Location[];
}

export interface Location {
  id: number;
  name: string;
  img_url: string;
}

export interface Character {
  tag: string;
  name: string;
  location: Location
}

export interface Suggestion {
  tag: string;
  description: string;
  examples: string[];
}

export interface Sound {
  id: number;
  name: string;
  url: string;
}

export interface Category {
  id: number;
  name: string;
}

export interface Script extends PublicScript {
  characters: Character[];
  backdrop: Backdrop;
  categories: string[];
  suggestions: Suggestion[];
  content: string;
  status: number;
  isAnyVersionPublished: boolean;
  version?: number;
  created_at?: string;
  is_draft?: boolean;
  staffComment?: string;
  authorComment?: string;
}


@Injectable({
  providedIn: 'root'
})
export class ScriptService {
  public static readonly CREATION_DEADLINE = 1779296400; // May 21, 2026
  public static readonly EDIT_DEADLINE = 1780246800;     // June 1, 2026

  public static isCreationAllowed(): boolean {
    return Math.floor(Date.now() / 1000) <= ScriptService.CREATION_DEADLINE;
  }

  public static isEditAllowed(): boolean {
    return Math.floor(Date.now() / 1000) <= ScriptService.EDIT_DEADLINE;
  }

  private api = inject(ApiService);

  public ownScripts: PublicScript[] = [];

  public sounds: Sound[] = [];

  public universal_locations: Location[] = [];

  public backdrops: Backdrop[] = [];

  public categories: Category[] = [];

  constructor() {
    this.loadEditorData();
    this.getOwnScripts();
    console.log('ScriptService initialized');
  }

  getOwnScripts() {
    this.api.get('scripts/ownScripts.php').subscribe((data: any) => {
      if (data && data.data) {
        this.ownScripts = data.data;
      } else {
        this.ownScripts = [];
      }
    }, (error) => {
      console.error('Failed to fetch own scripts:', error);
      this.ownScripts = [];
    });
  }


  loadEditorData(): void {
    this.api.get('scripts/getEditorData.php').subscribe({next: (response: any) => {
      if (response && response.code === 200 && response.data) {
        this.universal_locations = Array.isArray(response.data.universal_locations) ? response.data.universal_locations : [];
        this.backdrops = Array.isArray(response.data.backdrops) ? response.data.backdrops : [];
        this.sounds = Array.isArray(response.data.sounds) ? response.data.sounds : [];
        this.categories = Array.isArray(response.data.categories) ? response.data.categories : [];
      } else {
        // Fallback to debug data if API returns unexpected structure
        this.applyDebugEditorDataFallback();
      }
    }, error: (err) => {
      console.error('Failed to load editor data:', err);
      this.applyDebugEditorDataFallback();
    }});
  }

  private applyDebugEditorDataFallback(): void {
    if (this.universal_locations.length === 0) {
      this.universal_locations = debug_universal_locations;
    }
    if (this.backdrops.length === 0) {
      this.backdrops = debug_backdrops;
    }
    if (this.sounds.length === 0) {
      this.sounds = debug_sounds;
    }
    if (this.categories.length === 0) {
      const fallbackNames = ['Skit', 'Infomercial', 'Game Show', 'Comedy', 'Drama', 'Action', 'Romance', 'Horror', 'Sci-Fi', 'Fantasy'];
      this.categories = fallbackNames.map((name, index) => ({ id: index + 1, name }));
    }
  }


  getScript(id: number, versionId?: number): Observable<Script> {
    const url = versionId 
      ? `scripts/get.php?id=${id}&version_id=${versionId}`
      : `scripts/get.php?id=${id}`;
    
    return this.api.get(url).pipe(
      map((response: any) => {
      if (response.code == 200 && response.data) {
        return response.data as Script;
      } else {
        throw new Error('Failed to load script');
      }
    }));
  }

  getScriptVersions(id: number): Observable<any[]> {
    return this.api.get(`scripts/getVersions.php?id=${id}`).pipe(
      map((response: any) => {
      if (response.code == 200 && response.data) {
        return response.data;
      } else {
        throw new Error('Failed to load script versions');
      }
    }));
  }

  submitScript(scriptData: any, isDraft: boolean = false): Observable<any> {

    return this.api.post('scripts/submit.php', { data: scriptData, is_draft: isDraft }).pipe(
      map((response: any) => {
      // Refresh own scripts after successful submission to keep data in sync
      this.getOwnScripts();
      return response;
    }));
  }

  deleteScript(scriptId: number): Observable<any> {
    return this.api.post('scripts/delete.php', { script_id: scriptId }).pipe(
      map((response: any) => {
        // Refresh own scripts after successful deletion to keep data in sync
        this.getOwnScripts();
        return response;
      })
    );
  }

  canDeleteScript(script: PublicScript): boolean {
    // Script can be deleted if it has no currently published versions
    console.log("Can delete script:", !script.isAnyVersionPublished);
    return !script.isAnyVersionPublished;
  }

  getStatusText(script: PublicScript): { title: string, description: string, icon: {icon: string, tooltip: string}[] } {

    // Check if script contains only drafts first
    if (script.isOnlyDrafts) {
      return { title: 'Draft', description: 'This script is still in draft form and has not been submitted for review.', icon: [{icon: "edit", tooltip: "Draft"}] };
    }

    if (!script.status) {
      return { title: 'Unknown', description: 'This script is neither published nor rejected, but also not pending review', icon: [{icon: "help", tooltip: "Unknown"}] };
    }

    if (script.status & 8) {
      return { title: 'Published', description: 'Your script is published and can be played in the game.', icon: [{icon: "check_circle", tooltip: "Published"}] };
    }
    if (script.isAnyVersionPublished) {
      if (script.status & 4) {
        return { title: 'Published - Rejected', description: 'The most recent change to your script has been rejected. An older version of your script remains published.', icon: [{icon: "check_circle", tooltip: "Published"}, {icon: "close", tooltip: "Rejected Changes"}] };
      } else if (script.status & 1) {
        return { title: 'Published - Pending Review', description: 'Your script is waiting for review. An older version of your script remains published in the meantime.', icon: [{icon: "check_circle", tooltip: "Published"}, {icon: "pending_actions", tooltip: "Pending Review"}] };
      } else {
        return { title: 'Unknown', description: 'This script is neither published nor rejected, but also not pending review. Another version of your script is currently published', icon: [{icon: "help", tooltip: "Unknown"}] };
      }
    } else {
      if (script.status & 4) {
        return { title: 'Rejected', description: 'Your script has been rejected.', icon: [{icon: "close", tooltip: "Rejected"}] };
      } else if (script.status & 1) {
        return { title: 'Pending', description: 'Your script is waiting for review.', icon: [{icon: "pending", tooltip: "Pending Review"}] };
      } else {
        return { title: 'Unknown', description: 'This script is neither published nor rejected, but also not pending review', icon: [{icon: "help", tooltip: "Unknown"}] };
      }
    }
  }


}
